import { Component, inject, signal, effect, computed, resource, viewChild } from '@angular/core';
import {
  MarketingOrderNotesService, NoteForm, Note, NoteCategory, PromptDialogService, UIConstants,
} from '@lc/core';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatDivider } from '@angular/material/divider';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { TextareaComponent } from '../../../../../ui/src/lib/inputs/textarea/textarea.component';
import { SpinnerComponent, Spinner } from '../../../../../ui/src/lib/components/spinner/spinner.component';
import { NotesDialogService } from './notes-dialog.service';

type SortOption = 'date-desc' | 'date-asc' | 'category' | 'author';

@Component({
  selector: 'lc-notes-dialog',
  templateUrl: './notes-dialog.component.html',
  styleUrls: ['./notes-dialog.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButton,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatTabGroup,
    MatTab,
    MatDivider,
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    TextareaComponent,
    NgTemplateOutlet,
    TitleCasePipe,
  ],
})
export class NotesDialogComponent {
  private notesService = inject(MarketingOrderNotesService);
  private drawerService = inject(NotesDialogService);
  private promptDialog = inject(PromptDialogService);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  // ViewChild for filter menu trigger
  filterMenuTrigger = viewChild.required<MatMenuTrigger>('filterMenuTrigger');

  readonly drawerConfig = this.drawerService.config;

  readonly NoteCategory = NoteCategory;
  readonly categories = Object.values(NoteCategory);

  // Empty state constants
  readonly emptyStateTitle = 'No notes yet';
  readonly emptyStateMessage = 'Add a note to keep important agent and order information handy.';

  // State
  readonly selectedTabSignal = signal<'order' | 'agent'>('order');
  private selectedCategorySignal = signal<NoteCategory[]>([]);
  private tempSelectedCategorySignal = signal<NoteCategory[]>([]);
  readonly sortBySignal = signal<SortOption>('date-desc');

  readonly editingNoteId = signal<string | null>(null);
  readonly showAddNoteForm = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);

  readonly savingSpinner = computed(() => (this.isSaving() ? Spinner.loading() : Spinner.none()));

  // Computed signal for request parameters
  private requestParams = computed(() => ({
    config: this.drawerConfig(),
    tab: this.selectedTabSignal(),
  }));

  // Resource signal for loading notes
  private notesResource = resource({
    loader: async () => {
      const { config, tab } = this.requestParams();

      if (!config?.orderId) {
        return [];
      }

      const isAgentTab = tab === 'agent';

      if (isAgentTab && !config.agentId) {
        return [];
      }

      return isAgentTab
        ? await this.notesService.getAgentNotes(config.agentId!)
        : await this.notesService.getNotesFor(config.orderId);
    },
    defaultValue: [],
  });

  readonly loadingSpinner = computed(() => (this.notesResource.isLoading() ? Spinner.loading() : Spinner.none()));

  // Filtered and sorted notes computed signal
  filteredNotes = computed(() => {
    const notes = this.notesResource.value();
    const categories = this.selectedCategorySignal();
    const sort = this.sortBySignal();

    let filtered = notes;

    // Apply category filter
    if (categories.length > 0) {
      filtered = filtered.filter((note) => categories.includes(note.category as NoteCategory));
    }

    // Apply sorting
    return this.sortNotes(filtered, sort);
  });

  // Computed signal to track which notes have been edited (for performance)
  private editedNotesMap = computed(() => {
    const notes = this.notesResource.value();
    const editedMap = new Map<string, boolean>();
    notes.forEach((note) => {
      if (note.audit?.createdAt && note.audit?.updatedAt) {
        const createdTime = this.getTimestamp(note.audit.createdAt);
        const updatedTime = this.getTimestamp(note.audit.updatedAt);
        editedMap.set(note._id, createdTime !== updatedTime);
      } else {
        editedMap.set(note._id, false);
      }
    });
    return editedMap;
  });

  constructor() {
    // Register custom copy icon
    this.iconRegistry.addSvgIcon(
      'content_copy',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/content_copy.svg'),
    );

    // Initialize category validators based on tab
    this.updateCategoryValidators();

    // Watch for config changes and reset form
    effect(() => {
      const config = this.drawerConfig();
      if (config?.orderId) {
        // Reset form and editing state when switching orders
        this.resetForm();
      }
    });

    // Manually reload resource when dependencies change (async loaders don't auto-track)
    effect(() => {
      // Access the computed signal to track its dependencies
      this.requestParams();
      // Reload the resource
      this.notesResource.reload();
    });
  }

  readonly form: NoteForm = new NoteForm();

  closeDrawer() {
    this.drawerService.closeNotes();
  }

  closeFilterMenu() {
    this.filterMenuTrigger().closeMenu();
  }

  private updateCategoryValidators() {
    const categoryControl = this.form.get('category');
    if (this.selectedTabSignal() === 'order') {
      // Remove validation for order notes
      categoryControl?.clearValidators();
    } else {
      // Add validation for agent notes
      categoryControl?.setValidators([Validators.required]);
    }
    categoryControl?.updateValueAndValidity();
  }

  onTabChange(index: number) {
    const tab = index === 0 ? 'order' : 'agent';
    this.selectedTabSignal.set(tab);

    // Clear category filters when switching to order tab
    if (tab === 'order') {
      this.selectedCategorySignal.set([]);
      this.tempSelectedCategorySignal.set([]);
    }

    // Update category validators based on tab
    this.updateCategoryValidators();
  }

  async onSave() {
    this.form.markAllAsDirty();
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    const config = this.drawerConfig();
    if (!config?.orderId) {
      return;
    }

    const editingId = this.editingNoteId();
    const isAgentTab = this.selectedTabSignal() === 'agent';

    // Validate agent-specific requirements
    if (isAgentTab && !config.agentId) {
      console.error('Agent ID is required for agent notes');
      return;
    }

    this.isSaving.set(true);

    try {
      // Determine which service method to call
      if (isAgentTab) {
        await (editingId
          ? this.notesService.updateAgentNote(config.agentId!, editingId, this.form)
          : this.notesService.createAgentNote(config.agentId!, this.form));
      } else {
        await (editingId
          ? this.notesService.update(config.orderId, editingId, this.form)
          : this.notesService.create(config.orderId, this.form));
      }

      // Success: reset form and reload
      this.resetForm();
      this.notesResource.reload();
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  sortNotes(notes: Note[], sortOption: SortOption): Note[] {
    const sorted = [...notes];

    // Helper to get timestamp from note
    const getTimestamp = (note: Note): number => (note.audit?.createdAt ? new Date(note.audit.createdAt).getTime() : 0);

    switch (sortOption) {
      case 'date-desc':
        return sorted.sort((a, b) => getTimestamp(b) - getTimestamp(a));
      case 'date-asc':
        return sorted.sort((a, b) => getTimestamp(a) - getTimestamp(b));
      case 'category':
        return sorted.sort((a, b) => a.category.localeCompare(b.category));
      case 'author':
        return sorted.sort((a, b) => a.author.localeCompare(b.author));
      default:
        return sorted;
    }
  }

  onEdit(note: any) {
    this.editingNoteId.set(note._id);
    this.showAddNoteForm.set(false); // Close add form if open

    // Ensure validators are correct for current tab
    this.updateCategoryValidators();

    if (this.selectedTabSignal() === 'agent') {
      this.form.patchValue({
        text: note.text,
        category: note.category,
      });
    } else {
      this.form.patchValue({
        text: note.text,
      });
    }
  }

  async onCopy(note: any) {
    try {
      await navigator.clipboard.writeText(note.text);
      // Optionally show a success message
      console.log('Note copied to clipboard');
    } catch (error) {
      console.error('Failed to copy note:', error);
    }
  }

  async onDelete(note: any) {
    const response = await this.promptDialog.openPrompt(
      UIConstants.CONFIRM,
      'Are you sure you want to delete this notes',
      UIConstants.YES,
      [UIConstants.CANCEL],
    );

    if (response?.text !== UIConstants.YES) return;

    const config = this.drawerConfig();
    if (!config?.orderId) return;

    const isAgentTab = this.selectedTabSignal() === 'agent';
    if (isAgentTab && !config.agentId) return;

    await (isAgentTab
      ? this.notesService.deleteAgentNote(config.agentId!, note._id)
      : this.notesService.delete(config.orderId, note._id));

    this.notesResource.reload();
  }

  onFilterChange(category: NoteCategory) {
    const currentCategories = [...this.tempSelectedCategorySignal()];
    const index = currentCategories.indexOf(category);

    if (index > -1) {
      // Remove category if already selected
      currentCategories.splice(index, 1);
    } else {
      // Add category if not selected
      currentCategories.push(category);
    }

    this.tempSelectedCategorySignal.set(currentCategories);
  }

  isAllCategoriesSelected(): boolean {
    const selected = this.selectedCategorySignal();
    return selected.length === 0 || selected.length === this.categories.length;
  }

  isCategorySelected(category: NoteCategory): boolean {
    return this.tempSelectedCategorySignal().includes(category);
  }

  onApplyFilter() {
    this.selectedCategorySignal.set([...this.tempSelectedCategorySignal()]);
    this.filterMenuTrigger().closeMenu();
  }

  onClearFilter() {
    this.tempSelectedCategorySignal.set([]);
    this.selectedCategorySignal.set([]);
    this.filterMenuTrigger().closeMenu();
  }

  onFilterMenuOpened() {
    // Sync temp state with actual state when menu opens
    this.tempSelectedCategorySignal.set([...this.selectedCategorySignal()]);
  }

  toggleAllCategories() {
    if (this.isAllCategoriesSelected()) {
      // Deselect all
      this.selectedCategorySignal.set([]);
    } else {
      // Select all
      this.selectedCategorySignal.set([...this.categories]);
    }
  }

  onSortChange(sort: SortOption) {
    this.sortBySignal.set(sort);
  }

  resetForm() {
    this.form.reset();
    this.editingNoteId.set(null);
    this.showAddNoteForm.set(false);
    this.updateCategoryValidators();
  }

  onCancelForm() {
    this.resetForm();
  }

  getRelativeDate(dateString: string | undefined): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();

    // Helper to check if two dates are the same day
    const isSameDay = (d1: Date, d2: Date) => {
      return d1.getFullYear() === d2.getFullYear()
        && d1.getMonth() === d2.getMonth()
        && d1.getDate() === d2.getDate();
    };

    // Check if it's today
    if (isSameDay(date, now)) {
      return 'Today';
    }

    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (isSameDay(date, yesterday)) {
      return 'Yesterday';
    }

    // Check if it's tomorrow
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (isSameDay(date, tomorrow)) {
      return 'Tomorrow';
    }

    // Otherwise show date in M/D/YY format
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  }

  isNoteEdited(note: any): boolean {
    const cachedValue = this.editedNotesMap().get(note._id);
    if (cachedValue !== undefined) {
      return cachedValue;
    }
    // Fallback for notes not yet in the map (e.g., during tests or immediate after creation)
    if (note.audit?.createdAt && note.audit?.updatedAt) {
      const createdTime = this.getTimestamp(note.audit.createdAt);
      const updatedTime = this.getTimestamp(note.audit.updatedAt);
      return createdTime !== updatedTime;
    }
    return false;
  }

  // Helper to safely convert date string or Date object to timestamp
  private getTimestamp(date: string | Date | undefined): number {
    if (!date) return 0;
    if (date instanceof Date) return date.getTime();
    return new Date(date).getTime();
  }
}
